import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import SsExternalProgressPanel from '../../components/SsExternalProgressPanel';
import { resolveNetworkNav } from '../../navigation/networkNavigation';

const FLOW_LABEL = {
  no_job: 'No visit',
  scheduling: 'Scheduling',
  scheduled: 'Scheduled',
  progress: 'In progress',
  completed: 'Done',
};

function linkLabel(building) {
  const sources = building.link_sources || [];
  const bits = [];
  if (sources.includes('mandate')) bits.push('Service mandate');
  if (sources.includes('default_iqp')) bits.push('Default IQP');
  return bits.length ? bits.join(' · ') : '';
}

function partnerToForm(partner) {
  return {
    name: partner.name || '',
    iqp_name: partner.iqp_name || partner.contact_name || '',
    license_number: partner.license_number || '',
    email: partner.email || '',
    phone: partner.phone || '',
    address: partner.address || '',
    ss_codes: Array.isArray(partner.ss_codes) ? partner.ss_codes.join(', ') : '',
    notes: partner.notes || '',
  };
}

export default function PartnerDetail() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const selectedBuildingId = searchParams.get('building') || '';
  const selectedSs = searchParams.get('ss') || '';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['partner-companies', partnerId, 'work-board'],
    queryFn: async () => (await api.get(`/partner-companies/${partnerId}/work-board`)).data?.data,
    enabled: !!partnerId,
  });

  const partner = data?.partner;
  const buildings = data?.buildings || [];
  const selectedBuilding = buildings.find(
    (b) => String(b.building_id) === String(selectedBuildingId)
  );

  const openBuilding = (buildingId) => {
    const next = new URLSearchParams();
    next.set('building', String(buildingId));
    setSearchParams(next, { replace: false });
  };
  const openSs = (ssCode) => {
    const next = new URLSearchParams(searchParams);
    next.set('ss', ssCode);
    setSearchParams(next, { replace: false });
  };
  const clearSs = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('ss');
    setSearchParams(next, { replace: false });
  };
  const clearBuilding = () => {
    setSearchParams(new URLSearchParams(), { replace: false });
  };

  useEffect(() => {
    if (partner) setForm(partnerToForm(partner));
  }, [partner]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['partner-companies'] });
    queryClient.invalidateQueries({ queryKey: ['partner-companies', partnerId, 'work-board'] });
    queryClient.invalidateQueries({ queryKey: ['partner-companies', partnerId, 'buildings'] });
    queryClient.invalidateQueries({ queryKey: ['service-network'] });
    queryClient.invalidateQueries({ queryKey: ['mandates'] });
  };

  const saveMutation = useMutation({
    mutationFn: (body) => api.put(`/partner-companies/${partnerId}`, body),
    onSuccess: () => {
      toast.success('Partner saved');
      setEditing(false);
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save'),
  });

  const statusMutation = useMutation({
    mutationFn: (is_active) => api.put(`/partner-companies/${partnerId}`, { is_active }),
    onSuccess: (_res, is_active) => {
      toast.success(
        is_active
          ? 'Partner set to Active'
          : 'Partner set to Inactive ??cannot be assigned to new work; schedule auto-renew stopped'
      );
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update status'),
  });

  const handleSave = () => {
    if (!form?.name?.trim()) {
      toast.error('Company name is required');
      return;
    }
    const ss = form.ss_codes
      ? form.ss_codes
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    saveMutation.mutate({
      name: form.name.trim(),
      iqp_name: form.iqp_name || null,
      contact_name: form.iqp_name || null,
      license_number: form.license_number || null,
      email: form.email || null,
      phone: form.phone || null,
      address: form.address || null,
      ss_codes: ss,
      notes: form.notes || null,
    });
  };

  const toggleActive = () => {
    if (!partner) return;
    const next = !partner.is_active;
    if (
      !next &&
      !window.confirm(
        'Set this partner to Inactive?\n\nThey cannot be added to new mandates or SS assignments. Linked buildings will not get automatic schedule renewals after the current period.'
      )
    ) {
      return;
    }
    statusMutation.mutate(next);
  };

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading…</div>;
  }

  if (isError || !partner) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Partner not found</p>
        <button
          type="button"
          onClick={() => navigate('/network?tab=our-partner')}
          className="mt-4 text-primary-600"
        >
          Back to Our Partner
        </button>
      </div>
    );
  }

  const isActive = !!partner.is_active;

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveNetworkNav}
        title={partner.name}
        infoTitle="Our Partner"
        infoChildren={
          <div className="space-y-2 text-sm">
            <p>
              Partner company master record. Edit details here. <strong>Inactive</strong> partners cannot be assigned
              to new work; linked buildings will not auto-renew mandate schedules after the current period.
            </p>
          </div>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={toggleActive}
              disabled={statusMutation.isPending}
              className={`px-3 py-2 text-sm font-semibold rounded-lg border ${
                isActive
                  ? 'border-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100'
                  : 'border-emerald-300 text-emerald-900 bg-emerald-50 hover:bg-emerald-100'
              }`}
            >
              {statusMutation.isPending ? 'Updating…' : isActive ? 'Set Inactive' : 'Set Active'}
            </button>
            {!editing ? (
              <button
                type="button"
                onClick={() => {
                  setForm(partnerToForm(partner));
                  setEditing(true);
                }}
                className="px-3 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setForm(partnerToForm(partner));
                    setEditing(false);
                  }}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  className="px-3 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>
        }
      />

      {!isActive && (
        <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-950">
          This partner is <strong>Inactive</strong>. It cannot be selected for new mandates or SS assignees. Existing
          building links remain visible, but schedule auto-renew for those mandates is stopped.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-card rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Company information</h2>
            <span
              className={`text-xs font-semibold px-2 py-1 rounded-full ${
                isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          {editing && form ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <label className="sm:col-span-2 block">
                <span className="text-gray-600">Company *</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-gray-600">IQP</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.iqp_name}
                  onChange={(e) => setForm((f) => ({ ...f, iqp_name: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-gray-600">Licence</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.license_number}
                  onChange={(e) => setForm((f) => ({ ...f, license_number: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-gray-600">Email</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-gray-600">Phone</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </label>
              <label className="sm:col-span-2 block">
                <span className="text-gray-600">SS (comma-separated)</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.ss_codes}
                  onChange={(e) => setForm((f) => ({ ...f, ss_codes: e.target.value }))}
                  placeholder="SS 15.2, SS 3.1"
                />
              </label>
              <label className="sm:col-span-2 block">
                <span className="text-gray-600">Address</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                />
              </label>
              <label className="sm:col-span-2 block">
                <span className="text-gray-600">Notes</span>
                <textarea
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
            </div>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Company</dt>
                <dd className="text-gray-900 font-medium">{partner.name}</dd>
              </div>
              <div>
                <dt className="text-gray-500">IQP</dt>
                <dd className="text-gray-800">{partner.iqp_name || partner.contact_name || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Licence</dt>
                <dd className="text-gray-800">{partner.license_number || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">SS (master)</dt>
                <dd className="text-gray-800">
                  {(Array.isArray(partner.ss_codes) ? partner.ss_codes : []).join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="text-gray-800">{partner.email || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Phone</dt>
                <dd className="text-gray-800">{partner.phone || '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Address</dt>
                <dd className="text-gray-800">{partner.address || '—'}</dd>
              </div>
              {partner.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Notes</dt>
                  <dd className="text-gray-800 whitespace-pre-wrap">{partner.notes}</dd>
                </div>
              )}
            </dl>
          )}
        </section>

        <section className="bg-card rounded-lg border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedSs && selectedBuilding
                ? `${selectedSs} progress`
                : selectedBuilding
                  ? `SS at ${selectedBuilding.building_name}`
                  : `Buildings (${buildings.length})`}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              {selectedSs
                ? 'Mail and schedule steps for this external SS visit.'
                : selectedBuilding
                  ? 'SS this partner handles on this building. Open one for progress.'
                  : 'Buildings linked by Service mandates or Default IQP. Open a building, then an SS.'}
              {!isActive && ' Schedule auto-renew is paused while this partner is Inactive.'}
            </p>
          </div>

          {selectedSs && selectedBuilding ? (
            <div className="p-4">
              <SsExternalProgressPanel
                partnerId={partnerId}
                buildingId={selectedBuilding.building_id}
                ssCode={selectedSs}
                onClose={clearSs}
                compact
              />
            </div>
          ) : selectedBuilding ? (
            <>
              <div className="px-4 py-2 border-b border-gray-100">
                <button
                  type="button"
                  onClick={clearBuilding}
                  className="text-sm text-primary-700 hover:underline"
                >
                  ← All buildings
                </button>
              </div>
              {(selectedBuilding.ss_items || []).length === 0 ? (
                <p className="p-6 text-sm text-gray-500 text-center">No SS linked on this building.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {selectedBuilding.ss_items.map((ss) => (
                    <li key={ss.ss_code}>
                      <button
                        type="button"
                        onClick={() => openSs(ss.ss_code)}
                        className="w-full text-left p-4 sm:p-5 flex items-center gap-3 hover:bg-gray-50"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900">{ss.ss_code}</p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {FLOW_LABEL[ss.flow] || ss.flow}
                            {ss.scheduled_date ? ` · visit ${ss.scheduled_date}` : ''}
                            {ss.due_date ? ` · due ${ss.due_date}` : ''}
                            {ss.health === 'overdue' ? ' · overdue' : ''}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : buildings.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">
              Not linked yet. Add under Building → Service mandates, or set Default IQP on an SS to
              this partner.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {buildings.map((b) => (
                <li key={b.building_id}>
                  <button
                    type="button"
                    onClick={() => openBuilding(b.building_id)}
                    className="w-full text-left p-4 sm:p-5 flex items-start gap-3 hover:bg-gray-50 group"
                  >
                    <Building2 className="w-5 h-5 text-navy-800 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 group-hover:text-primary-800">
                        {b.building_name}
                      </p>
                      {b.address && <p className="text-sm text-gray-500 truncate">{b.address}</p>}
                      <p className="text-xs text-gray-600 mt-1">
                        {(b.ss_items || []).map((s) => s.ss_code).join(', ') || '—'}
                        {b.summary?.overdue ? ` · ${b.summary.overdue} overdue` : ''}
                        {b.summary?.waiting_mail ? ` · ${b.summary.waiting_mail} waiting` : ''}
                      </p>
                      {linkLabel(b) ? (
                        <p className="text-xs text-gray-500 mt-0.5">{linkLabel(b)}</p>
                      ) : null}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
            Or open{' '}
            <Link to="/buildings" className="text-primary-700 underline">
              Buildings
            </Link>{' '}
            list.
          </div>
        </section>
      </div>
    </div>
  );
}
